// covered-by: frontends/carrie/src/routes/ask-puntt/components/canvas/blocks/__tests__/MetricBlock.test.tsx
import { Text, Tooltip } from "@radix-ui/themes";
import cx from "classnames";
import { useRef, useState } from "react";

import {
  agentComponentIdAttr,
  type ChatAtomAgentComponentId,
  type ChatAtomAgentComponentInstanceId,
  CHAT_ATOM_AGENT_COMPONENT_IDS,
} from "../../../agentComponents/chatAtomRegistry";

function TruncatedDescription({
  text,
  testId,
}: {
  text: string;
  testId: string;
}) {
  const textRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);

  return (
    <Tooltip content={text} open={open} delayDuration={200}>
      <Text
        as="span"
        ref={textRef}
        size="1"
        className="min-w-0 truncate text-puntt-neutral-gray-11"
        data-testid={testId}
        onMouseEnter={() => {
          const el = textRef.current;
          // why: a description that already fits has nothing left to reveal
          if (el) setOpen(el.scrollWidth > el.clientWidth);
        }}
        onMouseLeave={() => setOpen(false)}
      >
        {text}
      </Text>
    </Tooltip>
  );
}

/**
 * Accent value + label in a single horizontal chip (no trend, no icon).
 */
export function MetricBlockCondensed({
  label,
  value,
  agentComponentId = CHAT_ATOM_AGENT_COMPONENT_IDS.metricBlockCondensed,
  className,
  "data-testid": dataTestId = "metric-block-condensed",
}: {
  label: string;
  value: string;
  agentComponentId?:
    | ChatAtomAgentComponentId
    | ChatAtomAgentComponentInstanceId;
  className?: string;
  "data-testid"?: string;
}) {
  return (
    <div
      {...agentComponentIdAttr(agentComponentId)}
      data-testid={dataTestId}
      data-size="compact"
      // why: Figma 49612:38 keeps white fill + neutral stroke; panel chrome stays bare.
      className={cx(
        "flex min-w-0 items-center gap-2 overflow-hidden rounded-lg border border-puntt-neutral-gray-6 bg-base-white px-3 py-2",
        className,
      )}
    >
      <Text
        as="span"
        size="3"
        weight="medium"
        className="shrink-0 text-puntt-accent-10"
        data-testid={`${dataTestId}-value`}
      >
        {value}
      </Text>
      <TruncatedDescription text={label} testId={`${dataTestId}-label`} />
    </div>
  );
}
